"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const Square_1 = __importDefault(require("@mui/icons-material/Square"));
const material_1 = require("@mui/material");
const x_charts_1 = require("@mui/x-charts");
const react_query_1 = require("@tanstack/react-query");
const dayjs_1 = __importDefault(require("dayjs"));
const isBetween_1 = __importDefault(require("dayjs/plugin/isBetween"));
const react_1 = require("react");
const react_router_1 = require("react-router");
const strings_1 = require("../../../../../constants/strings");
const dashboardQuery_1 = require("../../../../../queries/dashboardQuery");
const zustandStore_1 = require("../../../../../zustandStore");
const slices_1 = require("../../../../../zustandStore/slices");
dayjs_1.default.extend(isBetween_1.default);
function isQueryEnabled(isAccessible, dateRange) {
    return Boolean(isAccessible && dateRange.fromDate && dateRange.toDate);
}
const getCurrencySymbol = (locale, currency) => (0)
    .toLocaleString(locale, { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 })
    .replaceAll(/\d/g, '')
    .trim();
const formatDateForChart = (date, format) => {
    if (globalThis.cardProvider?.IS_HSBC_PROVIDER_SETUP && format === 'M/D') {
        return date.format(format.replace('M/D', 'D/M'));
    }
    return date.format(format);
};
const LineBarChart = () => {
    const theme = (0, material_1.useTheme)();
    const { teamId } = (0, react_router_1.useParams)();
    const [isMobileScreen, setIsMobileScreen] = (0, react_1.useState)(window.innerWidth < 650);
    const initialData = (0, react_router_1.useLoaderData)();
    const isCompanyDashboard = initialData && 'isCompanyDashboard' in initialData && initialData.isCompanyDashboard;
    const isCompanyTeamDashboard = initialData && 'isCompanyTeamDashboard' in initialData && initialData.isCompanyTeamDashboard;
    const isManagerDashboard = initialData && 'isManagerDashboard' in initialData && initialData.isManagerDashboard;
    const currency = initialData && 'currency' in initialData && initialData.currency;
    const settlements = initialData && 'settlements' in initialData && initialData.settlements;
    const { dashboardSettlements, setDashboardSettlements, timeSpan, setTimeSpan, dateRange, setDateRange, chartData, setChartData, } = (0, zustandStore_1.useZustand)(state => ({
        timeSpan: state.dashboard.timeSpan,
        setTimeSpan: state.setTimeSpan,
        dateRange: state.dashboard.dateRange,
        setDateRange: state.setDateRange,
        chartData: state.dashboard.chartData,
        setChartData: state.setChartData,
        dashboardSettlements: state.dashboard.settlements,
        setDashboardSettlements: state.setDashboardSettlements,
    }));
    const dailytickCount = isMobileScreen ? 7 : 15;
    const weeklyTickCount = isMobileScreen ? 4 : 7;
    const monthlyTickCount = isMobileScreen ? 6 : 12;
    const chartMetaData = {
        daily: { units: 'days', tickCount: dailytickCount },
        weekly: { units: 'weeks', tickCount: weeklyTickCount },
        monthly: { units: 'months', tickCount: monthlyTickCount },
    };
    const timeLengthArray = Array.from({ length: chartMetaData[timeSpan].tickCount });
    const { data: companySettlements } = (0, react_query_1.useQuery)({
        ...(0, dashboardQuery_1.getSettlementsForCompanyQuery)(dateRange),
        initialData: settlements,
        enabled: isQueryEnabled(isCompanyDashboard, dateRange),
    });
    const { data: teamSettlements } = (0, react_query_1.useQuery)({
        ...(0, dashboardQuery_1.getSettlementsForTeamQuery)({ ...dateRange, teamId: teamId || '' }),
        initialData: settlements,
        enabled: isQueryEnabled(isCompanyTeamDashboard, dateRange),
    });
    const { data: managerSettlements } = (0, react_query_1.useQuery)({
        ...(0, dashboardQuery_1.getSettlementsForTeamManagerQuery)({ ...dateRange, teamId: teamId || '' }),
        initialData: settlements,
        enabled: isQueryEnabled(isManagerDashboard, dateRange),
    });
    (0, react_1.useEffect)(() => {
        setDateRange({
            toDate: (0, dayjs_1.default)().toISOString(),
            fromDate: (0, dayjs_1.default)().subtract(timeLengthArray.length, chartMetaData[timeSpan].units).startOf('day').toISOString(),
        });
    }, [timeSpan]);
    (0, react_1.useEffect)(() => {
        if (isQueryEnabled(isCompanyDashboard, dateRange)) {
            setDashboardSettlements(companySettlements);
        }
        else if (isQueryEnabled(isCompanyTeamDashboard, dateRange)) {
            setDashboardSettlements(teamSettlements);
        }
        else if (isQueryEnabled(isManagerDashboard, dateRange)) {
            setDashboardSettlements(managerSettlements);
        }
    }, [companySettlements?.length, teamSettlements?.length, managerSettlements?.length]);
    (0, react_1.useEffect)(() => {
        const getStartDate = index => {
            if (timeSpan === 'monthly') {
                return (0, dayjs_1.default)()
                    .startOf('month')
                    .subtract(timeLengthArray.length - index - 1, chartMetaData[timeSpan].units);
            }
            if (timeSpan === 'weekly') {
                return (0, dayjs_1.default)()
                    .startOf('day')
                    .day(1)
                    .subtract(timeLengthArray.length - index - 1, chartMetaData[timeSpan].units);
            }
            return (0, dayjs_1.default)()
                .startOf('day')
                .subtract(timeLengthArray.length - index - 1, chartMetaData[timeSpan].units);
        };
        const getTotals = index => {
            const startDate = getStartDate(index);
            const oneAhead = (0, dayjs_1.default)(startDate).add(1, chartMetaData[timeSpan].units);
            const filteredSettlements = dashboardSettlements?.filter(settlement => (0, dayjs_1.default)(settlement.date).isBetween(startDate, oneAhead, null, '[)'));
            return {
                totalCount: filteredSettlements.length,
                totalSpent: filteredSettlements.reduce((output, settlement) => output + Number(settlement.amount), 0),
            };
        };
        const aggregatedData = timeLengthArray.map((_, index) => {
            const { totalCount, totalSpent } = getTotals(index);
            return {
                currency,
                totalSpent,
                totalCount,
                startDate: getStartDate(index),
            };
        });
        setChartData(aggregatedData);
    }, [isMobileScreen, currency, timeSpan, dashboardSettlements?.length]);
    const formatTicks = (timeSpan, index) => {
        const date = chartData?.[index]?.startDate;
        if (timeSpan === 'monthly') {
            return (0, dayjs_1.default)(date).month() === 11 && (0, dayjs_1.default)(date).isBefore((0, dayjs_1.default)().startOf('year'))
                ? formatDateForChart((0, dayjs_1.default)(date), "MMM'YY")
                : formatDateForChart((0, dayjs_1.default)(date), 'MMM');
        }
        if (timeSpan === 'weekly') {
            return `${formatDateForChart((0, dayjs_1.default)(date).day(1), 'M/D')} - ${formatDateForChart((0, dayjs_1.default)(date).day(7), 'M/D')}`;
        }
        return formatDateForChart((0, dayjs_1.default)(date), 'M/D');
    };
    const handleResize = () => {
        const isMobile = window.innerWidth < 650;
        if (isMobile !== isMobileScreen) {
            setIsMobileScreen(isMobile);
        }
    };
    (0, react_1.useEffect)(() => {
        window.addEventListener('resize', handleResize);
        setTimeSpan(slices_1.timeSpanOptions[0]?.value || '');
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsxs)(x_charts_1.ChartContainer, { "data-testid": `${timeSpan}-chart`, series: [
                    {
                        type: 'bar',
                        data: chartData.map(d => d.totalSpent),
                        color: '#CBD7DC',
                        label: 'Spent ',
                        id: 'spent',
                    },
                    {
                        type: 'line',
                        data: chartData.map(d => d.totalCount),
                        curve: 'linear',
                        color: theme.palette.tsGoColor.pinkDark,
                        showMark: false,
                        label: 'Transactions ',
                        yAxisId: 'y-2-axis-id',
                        id: 'transactions',
                    },
                ], xAxis: [
                    {
                        data: chartData.map((_, index) => formatTicks(timeSpan, index)),
                        scaleType: 'band',
                        id: 'x-axis-id',
                        tickPlacement: 'middle',
                        disableTicks: true,
                        position: 'bottom',
                    },
                ], yAxis: [
                    {
                        data: chartData.map(d => d.totalSpent),
                        scaleType: 'linear',
                        id: 'y-1-axis-id',
                        disableTicks: true,
                        tickNumber: 5,
                        min: 0,
                        position: 'left',
                    },
                    {
                        data: chartData
                            ?.map(d => d.totalCount)
                            ?.toSorted(function (a, b) {
                            return a - b;
                        }),
                        scaleType: 'linear',
                        id: 'y-2-axis-id',
                        disableTicks: true,
                        tickNumber: 10,
                        tickMinStep: 1,
                        position: 'right',
                    },
                ], height: 280, sx: {
                    margin: '5px',
                    [`& .${x_charts_1.lineElementClasses.root}`]: {
                        strokeWidth: 1,
                    },
                    '& .MuiChartsAxis-right .MuiChartsAxis-tickLabel': {
                        strokeWidth: '0.4',
                        fill: theme.palette.tsGoColor.pinkDark,
                    },
                    '& .MuiChartsAxis-tickContainer .MuiChartsAxis-tickLabel': {
                        fontSize: '10px !important',
                    },
                }, children: [(0, jsx_runtime_1.jsx)(x_charts_1.BarPlot, { "data-testid": "bar-plot" }), (0, jsx_runtime_1.jsx)(x_charts_1.LinePlot, { "data-testid": "line-plot" }), (0, jsx_runtime_1.jsx)(x_charts_1.MarkPlot, { "data-testid": "mark-plot" }), (0, jsx_runtime_1.jsx)(x_charts_1.ChartsYAxis, { axisId: "y-1-axis-id", "data-testid": "y-1-axis-id" }), (0, jsx_runtime_1.jsx)(x_charts_1.ChartsXAxis, { axisId: "x-axis-id", "data-testid": "y-1-axis-id" }), (0, jsx_runtime_1.jsx)(x_charts_1.ChartsYAxis, { axisId: "y-2-axis-id", "data-testid": "y-2-axis-id", tickNumber: 5 }), (0, jsx_runtime_1.jsx)(x_charts_1.ChartsTooltip, { trigger: "axis", "data-testid": "charts-tolltip" }), (0, jsx_runtime_1.jsx)(x_charts_1.LineHighlightPlot, { "data-testid": "line-highlight" })] }), (0, jsx_runtime_1.jsxs)(material_1.Grid, { container: true, sx: {
                    justifyContent: 'center',
                    pb: 0.8,
                    gap: 1,
                }, children: [(0, jsx_runtime_1.jsxs)(material_1.Grid, { sx: {
                            display: 'flex',
                            flexDirection: 'row',
                        }, children: [(0, jsx_runtime_1.jsx)(Square_1.default, { fontSize: "small", sx: { color: '#CBD7DC', alignSelf: 'center' } }), (0, jsx_runtime_1.jsx)(material_1.Typography, { children: `${strings_1.dashboardStrings.TOTAL_SPENT} (${getCurrencySymbol('en', currency)})` })] }), (0, jsx_runtime_1.jsxs)(material_1.Grid, { sx: {
                            display: 'flex',
                            flexDirection: 'row',
                        }, children: [(0, jsx_runtime_1.jsx)(Square_1.default, { fontSize: "small", sx: { color: theme.palette.tsGoColor.pinkDark, alignSelf: 'center' } }), (0, jsx_runtime_1.jsx)(material_1.Typography, { children: strings_1.dashboardStrings.NUMBER_OF_TRANSACTIONS })] })] })] }));
};
exports.default = LineBarChart;
